import { CURRENT_VERSION, emptyLessonProgress, EXPORT_VERSION, type ProgressState } from './types';

/**
 * 版の移行（progress-store.md §6.3）。migrations[n] は版 n を n + 1 に変える純粋関数。古い移行も消さない。
 * version 1 が現行なので、いまは空。
 */
export const migrations: Record<number, (v: unknown) => unknown> = {};

/**
 * エクスポートファイルの版の移行（§6.3）。exportMigrations[n] は版 n のファイルを n + 1 にする。
 * 1 → 2: lessons（レッスンの進捗）を空で足す。
 */
export const exportMigrations: Record<number, (v: unknown) => unknown> = {
  1: (v) => ({
    ...(typeof v === 'object' && v !== null ? v : {}),
    version: 2,
    lessons: emptyLessonProgress(),
  }),
};

/** エクスポートファイルを現行版（{@link EXPORT_VERSION}）まで移行する。 */
export function migrateExportToCurrent(
  value: unknown,
  table: Readonly<Record<number, (v: unknown) => unknown>> = exportMigrations,
): MigrateResult {
  return migrateToCurrent(value, table, EXPORT_VERSION);
}

export type MigrateResult =
  | { ok: true; value: unknown; from: number; migrated: boolean }
  | {
      ok: false;
      reason: 'newer-version' | 'no-version' | 'missing-migration';
      version: number | null;
    };

function versionOf(v: unknown): number | null {
  if (typeof v !== 'object' || v === null || !('version' in v)) return null;
  const ver = v.version;
  return typeof ver === 'number' && Number.isInteger(ver) ? ver : null;
}

/** `version` を持つ値を現行版まで移行する。新しい版は読まない。 */
export function migrateToCurrent(
  value: unknown,
  table: Readonly<Record<number, (v: unknown) => unknown>> = migrations,
  current: number = CURRENT_VERSION,
): MigrateResult {
  const from = versionOf(value);
  if (from === null) return { ok: false, reason: 'no-version', version: null };
  if (from > current) return { ok: false, reason: 'newer-version', version: from };
  let v = value;
  for (let n = from; n < current; n++) {
    const step = table[n];
    if (!step) return { ok: false, reason: 'missing-migration', version: n };
    v = step(v);
  }
  return { ok: true, value: v, from, migrated: from !== current };
}

/**
 * カード id の変更（formerIds）を当てる冪等な処理（§6.3 末尾）。
 * cards[旧] があり cards[新] が無ければ移す。history の cardId も書き換える。
 */
export function applyFormerIds(
  state: ProgressState,
  cards: readonly { id: string; formerIds?: readonly string[] }[],
): ProgressState {
  const rename = new Map<string, string>();
  for (const c of cards) for (const old of c.formerIds ?? []) rename.set(old, c.id);
  if (rename.size === 0) return state;
  let changed = false;
  const nextCards = { ...state.cards };
  for (const [old, id] of rename) {
    const rec = nextCards[old];
    if (rec && !nextCards[id]) {
      nextCards[id] = rec;
      delete nextCards[old];
      changed = true;
    }
  }
  const history = state.history.map((h) => {
    const id = rename.get(h.cardId);
    if (id === undefined) return h;
    changed = true;
    return { ...h, cardId: id };
  });
  return changed ? { ...state, cards: nextCards, history } : state;
}
